// ABOUTME: Admin page for editing site-wide Make It Right default tasks
// ABOUTME: Uses same drag-and-drop UI as family template editor

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, DragIndicator } from '@mui/icons-material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeItRightTemplateAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TaskItem {
  id: string;
  title: string;
}

const SortableTaskItem: React.FC<{
  task: TaskItem;
  onDelete: () => void;
  onChange: (title: string) => void;
  hasError: boolean;
}> = ({ task, onDelete, onChange, hasError }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <IconButton {...attributes} {...listeners} size="small" aria-label="Drag to reorder task">
        <DragIndicator />
      </IconButton>
      <TextField
        fullWidth
        label="Task description"
        value={task.title}
        onChange={(e) => onChange(e.target.value)}
        sx={{ mx: 1 }}
        error={hasError}
        helperText={hasError ? 'Task cannot be empty' : ''}
      />
      <IconButton onClick={onDelete} color="error" aria-label="Delete task">
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

export const AdminMakeItRightDefaults: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadDefaults = useCallback(async () => {
    try {
      setError('');
      const defaults = await makeItRightTemplateAPI.getSiteDefaults();
      setTasks(defaults.tasks.map((title, index) => ({ id: `task-${index}`, title })));
    } catch (err) {
      console.error('Failed to load defaults:', err);
      setError('Failed to load defaults. Please try again.');
    }
  }, []);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddTask = () => {
    setTasks([...tasks, { id: `task-${Date.now()}`, title: '' }]);
  };

  const handleDeleteTask = (id: string) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const handleUpdateTask = (id: string, title: string) => {
    setTasks(tasks.map(task => task.id === id ? { ...task, title } : task));
  };

  const handleSave = async () => {
    if (!user || tasks.length === 0) return;

    try {
      setLoading(true);
      setError('');
      setValidationError('');
      const taskTitles = tasks.map(t => t.title).filter(t => t.trim() !== '');

      if (taskTitles.length === 0) {
        setValidationError('Must have at least one default task with a description');
        setLoading(false);
        return;
      }

      await makeItRightTemplateAPI.updateSiteDefaults({ tasks: taskTitles });
      setError('Defaults updated successfully');
    } catch (err) {
      console.error('Failed to save defaults:', err);
      setError('Failed to save defaults. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Make It Right Default Tasks
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          These tasks are used as the starting template for new families
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Typography variant="body2" color={error.includes('successfully') ? 'success.main' : 'error'} sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {validationError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {validationError}
          </Typography>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
            {tasks.map((task) => (
              <SortableTaskItem
                key={task.id}
                task={task}
                onDelete={() => handleDeleteTask(task.id)}
                onChange={(title) => handleUpdateTask(task.id, title)}
                hasError={validationError !== '' && task.title.trim() === ''}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddTask}>
            Add Task
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || tasks.length === 0}>
            Save Defaults
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
